import { Vue, Component, InjectReactive, Prop, Inject } from 'vue-property-decorator';
import {
  Step, StepPager, Btn, Toolbar, Field, DropdownField, IStepComponent, FullScreenToggle, IMapping, IMappingDetail, UpdateAutosaveFunction, RowDoubleClickedEvent
} from '@samc/centricity-core';
import { AjaxHelper, AjaxHelperException } from '@samc/common';
import { QueryProps } from "@samc/querybuilder/core/ClientApp/modular/Context/QueryContext";
import { LsGrid } from '@/Components/LSGrid';
import ImportAutosave from '@/Store/ImportAutoSave';

@Component({
  name: 'validation-step',
  components: {
    Step, StepPager, Btn, Toolbar, Field, DropdownField, LsGrid, FullScreenToggle
  }
})
export default class ValidationStepComponent extends Vue implements IStepComponent {
  public initialized: boolean = false;
  public isDirty: boolean = false;
  public isReadonly: boolean = false;
  public merging: boolean = false;
  public scratchPadNode: string = '';
  public isValid: boolean = true;
  public lsBasicViewId: number = 3; //this is Basic View of L&S metadata

  @Prop({ required: true }) public domainId!: number;
  @Prop({ required: true }) public viewId!: number;
  @Prop({ required: true }) public scratchPadUrl!: string;
  @Prop({ required: true }) public lsUrl!: string;
  @Prop({ default: '' }) public packageId!: string;
  @Prop() public loanListId!: string;
  @Prop() public hasEnrichments!: string;
  @Prop() public value!: any;
  @Prop({ default: 'Validation' }) public title!: string;

  @Prop() public enrichedFileName!: string;
  @Prop() public fileName!: string;
  @Prop() public mergeFileName!: string;

  @InjectReactive() public readonly extraParams!: Record<string, string>;
  @InjectReactive() public mapping!: IMapping<IMappingDetail> | null;

  /**
   * The wrapper for the component instance
   */
  public get component(): IStepComponent | null {
    return {
      isValid: this.isValid,
      isDirty: this.isDirty,
      isReadonly: this.isReadonly,
      clear: this.clear,
      initializeEvents: this.initializeEvents,
      clearEvents: this.clearEvents,
      initialize: this.initialize,
      enter: this.enter,
      skip: this.skip,
      leave: this.leave
    };
  };
  public get scratchpadPath(): string { return `${this.packageId}/${this.mergeFileName}`; }
  public get description(): string { return this.value ? this.value.description : ''; }
  public get validationId(): number | undefined { return this.validationIdInternal ?? undefined; }

  private validationIdInternal: number | null = null; // please keep in mind that we cannot change this variable in any lsgrid before query execution callbacks becasue it may provide an infinite loop
  private currentValidationId: number | null = null; // this variable tracks the user choice of current validation id (validation view)
  
  private verificationToken: string = '';

  public async clear(): Promise<void> {
    this.$emit('input', null);

    this.initialized = false;
    this.isDirty = false;
    this.isReadonly = false;
    this.merging = false;
  }

  public async initialize(): Promise<boolean> {
    if (this.value != undefined) {
     
      if (this.extraParams.validationId) {
        this.currentValidationId = parseInt(this.extraParams.validationId);
      } else {
        this.currentValidationId = this.mapping?.validationTemplateId ?? this.currentValidationId;
      }
      if (this.currentValidationId !== this.validationIdInternal) {
        this.validationIdInternal = this.currentValidationId;
      }
    }
    
    this.initialized = true;
    return true;
  }

  public async mounted() {
    this.verificationToken = await this.$verificationToken();
  }

  public async enter(): Promise<void> {
    // On Enter Event
  }

  public async skip(): Promise<void> {
    // On Skip
  }

  /**
   * Intialize events for the event bus
   **/
  public async initializeEvents(): Promise<void> {
    // Do Nothing
  }

  /**
   * Remove events from the event bus
   **/
  public async clearEvents(): Promise<void> {
    // Do Nothing
  }

  public async leave(dir: 1 | -1): Promise<boolean> {
    return true;
  }

  public async createValidationNodes(params: QueryProps) {
    if (!this.merging) {
      try {
        this.merging = true;        
        if (this.hasEnrichments) {          
          const fields = await this.getValidationFields(params);
          await this.mergeData(fields);
        }
        if (params.validationId !== null && params.validationId !== undefined) {
          this.currentValidationId = params.validationId;
          const validationResp = await this.getValidationDetails(params.validationId);
          this.$emit('input', { id: params.validationId, description: validationResp.description });
          await ImportAutosave.updateAutosave();
        }
      } catch (ex: any) {
        if (ex instanceof AjaxHelperException && ex.data?.Messages) {
        //  //TODO: Suppressing the alert and must be reverted to original after fixing issue with missing primaryViewId
        //  this.$alert({
        //    type: 'error',
        //    title: 'The following errors occurred!',
        //    errors: ex.response.data.Messages
        //  });
        } else if (ex.message !== undefined && ex.message === 'canceled') {
          this.$log.error(ex);
        } else {
          //TODO: Suppressing the alert and must be reverted to original after fixing issue with missing primaryViewId
          //this.$alert.error('Error occurred during merge process to calculate validations from enrichment');
        }
        this.merging = false;
        return false;
      } finally {        
        this.merging = false;
        return true;
      }
    }
    
    return false;
  }

  private async getValidationFields(params: QueryProps) {
    const helper = new AjaxHelper(this.lsUrl, this.verificationToken, null, this.$token);
    return await helper.options('/api/Query/Execute', params);
  }

  private async getValidationDetails(validationId: number) {
    /// TODO: Move this request to the @samc/query-builder-client npm package with proper typing
    const helper = new AjaxHelper(this.lsUrl, this.verificationToken, null, this.$token);
    return helper.getData<any>('/api/ValidationTemplate/GetTemplate', { templateId: validationId });
  }

  private async mergeData(fields: any) {
    const data = {
      MergedFileName: this.mergeFileName,      // 10b
      PreviousFileName: this.enrichedFileName, // 9b
      FileName: this.fileName,                 // 10a
      PackageId: this.packageId,
      domainId: this.domainId,
      fields,
      externalSetIds: [this.loanListId]
    };

    /// TODO: Move this request to the @samc/query-builder-client npm package with proper typing
    const qbhelper = new AjaxHelper(this.lsUrl, this.verificationToken, null, this.$token);
    await qbhelper.postData(`/api/DynamicScratchpad/ExecAndMerge`, data, { withCredentials: true });
  }
}